import {
  analyzePageApiV1DiagnosticAnalyzePost,
  deleteAnalysisApiV1DiagnosticAnalysesAnalysisIdDelete,
  getAnalysisApiV1DiagnosticAnalysesAnalysisIdGet,
  getJobStatusApiV1DiagnosticJobsJobIdGet,
  listAnalysesApiV1DiagnosticAnalysesGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  DiagnosticAnalysisDetailResponse,
  DiagnosticAnalysisListItem,
  DiagnosticJobStatusResponse,
  DiagnosticPageType,
  DiagnosticResult,
} from "@/backend-sdk/client/types.gen";
import {
  AiCreditsExhaustedError,
  STATUS_SEM_CREDITOS,
} from "@/services/ai-credit-packs.service";

/**
 * Serviço do Diagnóstico (auditoria de CRO das páginas da loja).
 *
 * Usa o SDK tipado gerado do OpenAPI do backend, que por sua vez faz proxy para
 * o Nexus. Fluxo: analisar (retorna job_id na hora) -> polling do status até
 * COMPLETED/FAILED -> resultado renderizado nas abas.
 */

// Os endpoints do Diagnóstico não dependem do período selecionado na tela, então
// os filtros globais que o cliente HTTP anexa só atrapalhariam.
const IGNORE_FILTERS_HEADERS = { "x-ignore-global-filters": "1" } as const;

/** customer_uuid da conta/subconta selecionada (mesmo valor que os dashboards). */
function activeCustomerUuid(): string | undefined {
  return localStorage.getItem("active_customer_uuid") ?? undefined;
}

function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export type DiagnosticJobStatus =
  | "QUEUED"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED";

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);

/**
 * O OpenAPI tipa `status` como `string`. Um valor novo do backend (ex.:
 * `CANCELLED`) tratado como "em andamento" deixaria a UI girando até o timeout
 * de 6 minutos, então qualquer status desconhecido e não-terminal é normalizado
 * para FAILED, que a tela sabe exibir.
 */
function toJobStatus(raw: string): DiagnosticJobStatus {
  if (raw === "QUEUED" || raw === "PROCESSING") return raw;
  if (TERMINAL_STATUSES.has(raw)) return raw as DiagnosticJobStatus;
  return "FAILED";
}

export type { DiagnosticPageType, DiagnosticResult };

/**
 * `DiagnosticResult` com as listas garantidas.
 *
 * No OpenAPI os arrays são opcionais (o backend os declara com
 * `default_factory=list`), o que obrigaria a tela a checar `undefined` em todo
 * acesso. Normalizamos uma vez aqui, na fronteira do serviço.
 */
export type DiagnosticResultFull = Omit<
  DiagnosticResult,
  "problemas" | "oportunidades" | "checklist" | "plano_acao"
> & {
  problemas: NonNullable<DiagnosticResult["problemas"]>;
  oportunidades: NonNullable<DiagnosticResult["oportunidades"]>;
  checklist: NonNullable<DiagnosticResult["checklist"]>;
  plano_acao: NonNullable<DiagnosticResult["plano_acao"]>;
};

function normalizeResult(
  result: DiagnosticResult | null | undefined,
): DiagnosticResultFull | null {
  if (!result) return null;
  return {
    ...result,
    problemas: result.problemas ?? [],
    oportunidades: result.oportunidades ?? [],
    checklist: result.checklist ?? [],
    plano_acao: result.plano_acao ?? [],
  };
}

export interface DiagnosticPhase {
  key: string;
  label: string;
  at: string;
}

export interface DiagnosticJob {
  jobId: string;
  analysisId: string;
  pageType: DiagnosticPageType;
  url: string;
  status: DiagnosticJobStatus;
  phases: DiagnosticPhase[];
  result?: DiagnosticResultFull | null;
  score?: number | null;
  error?: string | null;
  createdAt?: string | null;
  finishedAt?: string | null;
  // Saldo de créditos após o débito desta auditoria (null = plano ilimitado).
  creditsRemaining?: number | null;
}

export interface DiagnosticAnalysisSummary {
  analysisId: string;
  siteUrl: string;
  scoreGeral: number | null;
  pageTypes: DiagnosticPageType[];
  updatedAt: string | null;
}

export interface DiagnosticAnalysisDetail {
  analysisId: string;
  siteUrl: string;
  scoreGeral: number | null;
  createdAt: string | null;
  pages: DiagnosticJob[];
}

/** Rótulos das abas. Caixa alta segue o padrão das demais telas (ex.: SEOPage). */
export const PAGE_TYPE_LABEL: Record<DiagnosticPageType, string> = {
  home: "HOMEPAGE",
  category: "CATEGORIA",
  product: "PRODUTO",
};

function mapJob(data: DiagnosticJobStatusResponse): DiagnosticJob {
  return {
    jobId: String(data.job_id),
    analysisId: String(data.analysis_id),
    pageType: data.page_type,
    url: String(data.url),
    status: toJobStatus(String(data.status)),
    phases: data.phases ?? [],
    result: normalizeResult(data.result),
    score: data.score ?? null,
    error: data.error ?? null,
    createdAt: data.created_at ?? null,
    finishedAt: data.finished_at ?? null,
  };
}

class DiagnosticService {
  /** Dispara a auditoria de uma página. Retorna o job para acompanhar. */
  async analyze(
    pageType: DiagnosticPageType,
    url: string,
    analysisId?: string,
  ): Promise<DiagnosticJob> {
    const { data, error, response } = await analyzePageApiV1DiagnosticAnalyzePost({
      headers: IGNORE_FILTERS_HEADERS,
      body: {
        page_type: pageType,
        url,
        customer_uuid: activeCustomerUuid(),
        analysis_id: analysisId,
      },
    });
    if (error || !data) {
      // Repassa o detalhe do backend (ex.: "Créditos de IA insuficientes...",
      // "A página respondeu com status 403").
      const message = errorDetail(error, "Não foi possível iniciar a análise");
      // <M-280> Sem crédito não é falha: a tela oferece a compra.
      if (response?.status === STATUS_SEM_CREDITOS) {
        throw new AiCreditsExhaustedError(message);
      }
      throw new Error(message);
    }
    return {
      jobId: String(data.job_id),
      analysisId: String(data.analysis_id),
      pageType: data.page_type,
      url,
      status: toJobStatus(String(data.status)),
      phases: [],
      creditsRemaining: data.credits_remaining ?? null,
    };
  }

  async getJobStatus(jobId: string): Promise<DiagnosticJob> {
    const { data, error } = await getJobStatusApiV1DiagnosticJobsJobIdGet({
      headers: IGNORE_FILTERS_HEADERS,
      path: { job_id: jobId },
    });
    if (error || !data) {
      throw new Error("Não foi possível consultar o status da análise");
    }
    return mapJob(data as DiagnosticJobStatusResponse);
  }

  /** Histórico de análises da conta selecionada. */
  async listAnalyses(): Promise<DiagnosticAnalysisSummary[]> {
    const customerUuid = activeCustomerUuid();
    const { data, error } = await listAnalysesApiV1DiagnosticAnalysesGet({
      headers: IGNORE_FILTERS_HEADERS,
      query: customerUuid ? { customer_uuid: customerUuid } : {},
    });
    if (error || !data) {
      throw new Error("Não foi possível carregar o histórico de análises");
    }
    return (data.analyses ?? []).map((item: DiagnosticAnalysisListItem) => ({
      analysisId: String(item.analysis_id),
      siteUrl: String(item.site_url),
      scoreGeral: item.score_geral ?? null,
      pageTypes: item.page_types ?? [],
      updatedAt: item.updated_at ?? null,
    }));
  }

  /** Uma análise com o resultado mais recente de cada tipo de página. */
  async getAnalysis(analysisId: string): Promise<DiagnosticAnalysisDetail> {
    const { data, error } = await getAnalysisApiV1DiagnosticAnalysesAnalysisIdGet({
      headers: IGNORE_FILTERS_HEADERS,
      path: { analysis_id: analysisId },
    });
    if (error || !data) {
      throw new Error("Não foi possível carregar a análise");
    }
    const detail = data as DiagnosticAnalysisDetailResponse;
    return {
      analysisId: String(detail.analysis_id),
      siteUrl: String(detail.site_url),
      scoreGeral: detail.score_geral ?? null,
      createdAt: detail.created_at ?? null,
      pages: (detail.pages ?? []).map(mapJob),
    };
  }

  async deleteAnalysis(analysisId: string): Promise<void> {
    const { error } = await deleteAnalysisApiV1DiagnosticAnalysesAnalysisIdDelete({
      headers: IGNORE_FILTERS_HEADERS,
      path: { analysis_id: analysisId },
    });
    if (error) throw new Error("Não foi possível apagar a análise");
  }
}

export const diagnosticService = new DiagnosticService();
